/**
 * One capability screen, generated from its config.
 *
 * It renders each of the capability's sections as a panel: a small create form over the list it
 * writes into. The form and list both talk to the SAME path — POST creates, GET lists — and a
 * refusal is surfaced in full, message AND hint, because when a vendor seam is configured but has no
 * credentials the API answers 503 with exactly the hint that says what to do ("leave the vendor URL
 * blank to use the native store, or wait for the credential vault"). Swallowing the hint would throw
 * away the half of the message that helps.
 */

import { useState } from "react";
import { api, useApi, ApiError } from "../lib/api";
import type { CapabilityConfig, Section } from "../lib/capabilities";
import { ObservabilityProvider } from "./ObservabilityProvider";
import { NeedsRole, useGrant } from "../lib/grant";

const inputStyle: React.CSSProperties = {
  padding: 8, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)",
};

function SectionPanel({ section }: { section: Section }) {
  const { data, loading, error, reload } = useApi<Record<string, unknown[]>>(section.path);
  const grant = useGrant(section.path);
  const [values, setValues] = useState<Record<string, string>>(
    () => Object.fromEntries(section.fields.map((f) => [f.name, f.default ?? ""])),
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      const body: Record<string, unknown> = {};
      for (const f of section.fields) {
        body[f.name] = f.type === "number" ? Number(values[f.name]) : values[f.name];
      }
      await api(section.path, { method: "POST", body: JSON.stringify(body) });
      reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? `${err.message}${err.hint ? ` — ${err.hint}` : ""}` : String(err));
    } finally {
      setBusy(false);
    }
  }

  const rows = (data?.[section.listKey] ?? []) as Record<string, unknown>[];

  return (
    <div className="panel" data-testid="section" data-path={section.path}>
      <h2>{section.heading}</h2>
      <p className="muted">{section.blurb}</p>
      {/* The form only when the session's role could submit it (the one table in @plat/core);
          otherwise one line naming the role that could. The API's 403 holds either way. */}
      {grant.allowed ? (
        <form onSubmit={create} data-testid="write-form" data-path={section.path} style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          {section.fields.map((f) => (
            <input
              key={f.name}
              value={values[f.name] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              placeholder={f.label}
              aria-label={f.label}
              type={f.type === "number" ? "number" : f.type === "password" ? "password" : "text"}
              autoComplete={f.type === "password" ? "off" : undefined}
              required
              style={{ ...inputStyle, flex: f.width ? undefined : 1, width: f.width }}
            />
          ))}
          <button disabled={busy} type="submit"
            style={{ padding: "8px 16px", background: "var(--accent)", border: 0, borderRadius: 6, color: "#fff", cursor: "pointer" }}>
            {busy ? "…" : "Submit"}
          </button>
        </form>
      ) : <NeedsRole path={section.path} />}
      {problem && <p className="err" style={{ marginTop: 8 }}>{problem}</p>}

      <div style={{ marginTop: 16 }}>
        {loading && <p className="muted">Loading…</p>}
        {error && <p className="err">{error.message}{error.hint ? ` — ${error.hint}` : ""}</p>}
        {data && rows.length === 0 && <p className="muted">No {section.itemNoun}s yet.</p>}
        {data && rows.length > 0 && (
          <table>
            <thead><tr>{section.columns.map((c) => <th key={c.header}>{c.header}</th>)}</tr></thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={String(row["id"] ?? i)}>
                  {section.columns.map((c) => <td key={c.header}>{c.render(row)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function CapabilityView({ config }: { config: CapabilityConfig }) {
  return (
    <>
      <div className="panel">
        <h2>{config.title}</h2>
        <p className="muted">{config.description}</p>
        <p className="muted" style={{ marginTop: 8, fontSize: 13 }}>
          {config.note ?? (
            <>
              Backed by the native per-tenant store (row-level-security isolated). Set this capability's
              vendor URL to route it at the vendor seam instead — which today refuses honestly, pending
              the Phase-2 credential vault.
            </>
          )}
        </p>
      </div>
      {config.id === "observability" && <ObservabilityProvider />}
      {config.sections.map((s) => <SectionPanel key={s.heading} section={s} />)}
    </>
  );
}
